import { useState, KeyboardEvent, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton, FavoriteIcon, Divider, DeleteSavedSearchInfo } from '@/components';

import {
  Dialog,
  BodyLarge,
  BodyLargeEmphasized,
  ButtonFlatSmall,
  SearchBox,
} from '@/design-system';
import { schoolCategories } from '@/mock/schoolCategoriesMock';
import { communityCategories } from '@/mock/communityCategoriesMock';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { cx } from '@emotion/css';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { debounce, sortBy } from 'lodash';
import FlyersContext from '@/context/FlyersContext';
import SearchIcon from '@mui/icons-material/Search';
import { FlyerCategory } from '@/types/flyer';
import { Flyer } from '@/graphql/types';
import { APP_ROUTES } from '@/config/constants';
import styles from './FlyersAutocomplete.styles';

interface FlyersAutocompleteProps {
  trackId: string;
  initialTextSearch?: string;
  goToResults: (search: string, origin: string) => void;
  flyers: Array<Flyer>;
  children?: React.ReactNode;
  hideBackButton?: boolean;
  onResetTextSearch?: () => void;
}

type AutocompleteResults = {
  value: string;
  type: 'savedSearch' | 'flyerResults' | 'suggestedCategory';
};

export const FlyersAutocomplete: React.FC<FlyersAutocompleteProps> = ({
  trackId,
  initialTextSearch,
  goToResults,
  hideBackButton = false,
  flyers,
  children,
  onResetTextSearch,
}) => {
  const [textSearch, setTextSearch] = useState(initialTextSearch || '');
  const [autocomplete, setAutocomplete] = useState<Array<AutocompleteResults>>([]);
  const [savedSearchRemoval, setSavedSearchRemoval] = useState<string | null>();

  const { track } = useAnalytics();
  const navigate = useNavigate();
  const MATCH_PERCENTAGE = 35;
  const { savedSearches, toggleSavedSearches } = useContext(FlyersContext);

  const checkResultExist = (results: AutocompleteResults[], term: string) => {
    return results.some(({ value }) => value.toLowerCase() === term.trim().toLowerCase());
  };

  const normalizeResult = (text: string) =>
    text
      .replace(/[^a-zA-Z0-9/ ]/g, '')
      .trim()
      .toLowerCase();

  const setAutocompleteOptions = () => {
    if (textSearch.length > 0) {
      const textSearchLowerCase = textSearch.toLowerCase();
      const results: AutocompleteResults[] = [];
      if (savedSearches.length > 0) {
        savedSearches
          .filter(({ search }) => search.toLowerCase().includes(textSearchLowerCase))
          .forEach(({ search }) => {
            if (search.toLowerCase().startsWith(textSearchLowerCase)) {
              results.push({ value: search, type: 'savedSearch' });
            }
          });
      }

      sortBy(
        flyers.filter(({ title }) => title.toLowerCase().includes(textSearch.toLowerCase())),
        flyer => flyer.title
      ).forEach(({ title }) => {
        let normalizedResult = '';
        if (textSearchLowerCase.length === 1) {
          title.split(' ').forEach(word => {
            if (word.length > 2 && word.toLowerCase().startsWith(textSearchLowerCase)) {
              normalizedResult = normalizeResult(word);
              if (!checkResultExist(results, normalizedResult)) {
                results.push({ value: normalizedResult, type: 'flyerResults' });
              }
            }
          });
        } else {
          const startPosition = title.toLowerCase().indexOf(textSearchLowerCase);
          const titleSlice = title.slice(startPosition, title.length);
          const words = titleSlice.split(' ');
          const textSearchTerms = textSearch.split(' ');

          if (textSearchTerms.length === 1) {
            words.forEach((word, index) => {
              if (word.toLowerCase().startsWith(textSearchLowerCase)) {
                let term = '';
                const percent = (textSearch.length / word.length) * 100;
                if (percent >= MATCH_PERCENTAGE) {
                  normalizedResult = normalizeResult(word);
                  if (!checkResultExist(results, normalizedResult) && normalizeResult.length > 2) {
                    results.push({
                      value: normalizedResult,
                      type: 'flyerResults',
                    });
                  }
                }
                if (percent >= MATCH_PERCENTAGE && index < words.length - 1) {
                  if (titleSlice.includes(' ')) {
                    const titleArray = title.split(' ');
                    const pos = titleArray.findIndex(w =>
                      w.toLowerCase().startsWith(textSearchLowerCase)
                    );
                    if (pos >= 0) {
                      term = titleArray.slice(pos, titleArray.length).join(' ');
                    }
                  } else {
                    term = titleSlice;
                  }
                } else {
                  term = word;
                }

                if (term.length > 0 && term.length > 2) {
                  normalizedResult = normalizeResult(term);
                  if (!checkResultExist(results, normalizedResult)) {
                    results.push({
                      value: normalizedResult,
                      type: 'flyerResults',
                    });
                  }
                }
              }
            });
          } else {
            normalizedResult = normalizeResult(titleSlice);
            if (!checkResultExist(results, normalizedResult)) {
              results.push({
                value: normalizedResult,
                type: 'flyerResults',
              });
            }
          }
        }
      });
      const suggestedCategoriesList = getSuggestedCategories();
      suggestedCategoriesList.map(category =>
        results.push({ value: category.title, type: 'suggestedCategory' })
      );
      setAutocomplete(results);
    } else {
      setAutocomplete([]);
    }
  };

  const getSuggestedCategories = () => {
    if (textSearch.length > 1) {
      const allCategories = [...schoolCategories, ...communityCategories];
      const filteredCategories = allCategories.filter(category =>
        category.title.toLowerCase().includes(textSearch.toLowerCase())
      );
      const categoryIds = [...new Set(filteredCategories.map(category => category.id))];

      const categoryIdsWithResults = categoryIds.filter(categoryId =>
        flyers.some(flyer => flyer.categories.includes(categoryId))
      );

      const suggestedCategoriesList: FlyerCategory[] = [];
      categoryIdsWithResults.forEach(categoryId => {
        const category = allCategories.find(category => category.id === categoryId);
        if (category) suggestedCategoriesList.push(category);
      });

      return sortBy(suggestedCategoriesList, category => category.title);
    }
    return [];
  };

  const debounceSetAutocompleteOptions = debounce(setAutocompleteOptions, 500);

  useEffect(() => {
    debounceSetAutocompleteOptions();
  }, [textSearch]);

  const onBlurInputSearch = () => {
    if (textSearch) {
      track({
        action: GenericTrackTypes.TYPING,
        category: `${trackId}:search:input_search`,
        label: textSearch,
      });
    }
  };

  const resetTextSearch = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:search:input_search`,
      label: 'Cancel Search',
    });
    setTextSearch('');
    navigate(APP_ROUTES.home);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      goToResults(textSearch, 'autocomplete_results');
    }
  };

  const highlightText = (text: string) => {
    const result = text.replace(new RegExp(textSearch.trim(), 'gi'), match => {
      return `<span>${match}</span>`;
    });
    return (
      <span
        className={styles.highlightText}
        data-testid="autocompleteOption"
        dangerouslySetInnerHTML={{ __html: result }}
      />
    );
  };

  const onChange = (value: string) => {
    setTextSearch(value);
  };

  const handleDeleteSavedSearch = () => {
    if (savedSearchRemoval) {
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:search:remove_saved_search`,
        label: savedSearchRemoval,
      });
      setAutocomplete(autocomplete.filter(a => a.value !== savedSearchRemoval));
      toggleSavedSearches(savedSearchRemoval);
      setSavedSearchRemoval(null);
    }
  };

  const savedSearchAC = autocomplete.filter(a => a.type === 'savedSearch');
  const suggestedCategories = autocomplete.filter(a => a.type === 'suggestedCategory');
  const flyerResultsAC = sortBy(
    autocomplete.filter(a => a.type === 'flyerResults'),
    a => a.value
  ).slice(0, 5);

  return (
    <>
      <Dialog
        isOpen={!!savedSearchRemoval}
        title={DeleteSavedSearchInfo.title}
        description={DeleteSavedSearchInfo.description}
        confirmButtonText={DeleteSavedSearchInfo.confirmButtonText}
        onCancel={() => setSavedSearchRemoval(null)}
        onConfirm={handleDeleteSavedSearch}
      />
      <div className={styles.container}>
        {!hideBackButton && textSearch.length === 0 && <BackButton trackId={trackId} />}
        <SearchBox
          placeholder="Search"
          value={textSearch}
          onChange={onChange}
          autoFocus
          showCancelButton
          onBlur={onBlurInputSearch}
          onKeyDown={onKeyDown}
          onResetTextSearch={onResetTextSearch}
        />
        {(hideBackButton || textSearch.length > 0) && (
          <ButtonFlatSmall
            onClick={resetTextSearch}
            className={styles.buttonCancelSpacing}
            data-testid="cancelSearch"
          >
            Cancel
          </ButtonFlatSmall>
        )}
      </div>
      <div className={styles.results}>
        {textSearch.length > 0 ? (
          <div className={styles.resultsContainer}>
            {savedSearchAC.length > 0 && (
              <section>
                {savedSearchAC.map(({ value }) => (
                  <div className={styles.savedSearches}>
                    <div
                      className={cx(styles.searchItem, styles.textBold)}
                      onClick={() => goToResults(value, 'autocomplete_results')}
                      key={value}
                    >
                      <FavoriteIcon size={22} isFavorite />
                      <BodyLarge nonTranslate>{value}</BodyLarge>
                    </div>
                    <ButtonFlatSmall
                      onClick={() => {
                        setSavedSearchRemoval(value);
                      }}
                      data-testid="savedSearchDeleteButton"
                    >
                      Delete
                    </ButtonFlatSmall>
                  </div>
                ))}
              </section>
            )}
            <section>
              {flyerResultsAC.map(({ value }) => (
                <div
                  className={cx(styles.searchItem)}
                  onClick={() => goToResults(value, 'autocomplete_results')}
                  key={value}
                >
                  <SearchIcon sx={{ fontSize: 22 }} className={styles.iconStone} />
                  {highlightText(value)}
                </div>
              ))}
            </section>
            {suggestedCategories.length > 0 &&
              (flyerResultsAC.length > 0 || savedSearchAC.length > 0) && (
                <div className={styles.separator}>
                  <Divider />
                </div>
              )}
            {suggestedCategories.length > 0 && (
              <section>
                <BodyLargeEmphasized>Suggested Categories</BodyLargeEmphasized>
                <div className={styles.suggestedCategoriesResults}>
                  {suggestedCategories.map(({ value }) => (
                    <div
                      className={cx(styles.searchItem, styles.textBold)}
                      onClick={() => goToResults(value, 'autocomplete_results')}
                      key={value}
                    >
                      <CategoryOutlinedIcon sx={{ fontSize: 22 }} className={styles.iconStone} />
                      <BodyLarge nonTranslate>{value}</BodyLarge>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </>
  );
};
